import type { PlanCode, RuntimeHealth, WorkspaceData } from "./types";

async function requestJson<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, { credentials: "same-origin", ...init });
  const contentType = response.headers.get("content-type") || "";
  const body = contentType.includes("application/json") ? await response.json() : { error: await response.text() };
  if (!response.ok) throw new Error(body.error || "The requested action could not be completed.");
  return body as T;
}

const jsonHeaders = { "content-type": "application/json" };

export function loadWorkspace(): Promise<WorkspaceData> {
  return requestJson<WorkspaceData>("/api/workspace");
}

export function loadRuntimeHealth(): Promise<RuntimeHealth> {
  return requestJson<RuntimeHealth>("/api/health");
}

export function createRecord<T>(entity: string, payload: Record<string, unknown>) {
  return requestJson<T>(`/api/workspace/${encodeURIComponent(entity)}`, {
    method: "POST",
    headers: jsonHeaders,
    body: JSON.stringify(payload),
  });
}

export function updateRecord<T>(entity: string, id: string | null, payload: Record<string, unknown>) {
  const path = entity === "profiles" ? "/api/workspace/profiles" : `/api/workspace/${encodeURIComponent(entity)}/${encodeURIComponent(id || "")}`;
  return requestJson<T>(path, { method: "PATCH", headers: jsonHeaders, body: JSON.stringify(payload) });
}

export function deleteRecord(entity: string, id: string) {
  return requestJson<{ ok: true }>(`/api/workspace/${encodeURIComponent(entity)}/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export function uploadAttachment(file: File, campaignId?: string) {
  const form = new FormData();
  form.set("file", file);
  if (campaignId) form.set("campaignId", campaignId);
  return requestJson<{ id: string }>("/api/attachments", { method: "POST", body: form });
}

export function createSubscription(plan: Exclude<PlanCode, "free">, interval: "monthly" | "annual") {
  return requestJson<{ keyId: string; subscriptionId: string; planName: string; userEmail: string }>("/api/create-subscription", {
    method: "POST", headers: jsonHeaders, body: JSON.stringify({ plan, interval }),
  });
}

export function verifySubscriptionPayment(payload: { razorpay_payment_id: string; razorpay_subscription_id: string; razorpay_signature: string }) {
  return requestJson<{ verified: true; plan: PlanCode }>("/api/verify-subscription", {
    method: "POST", headers: jsonHeaders, body: JSON.stringify(payload),
  });
}

function postAccountAction(path: string, payload: Record<string, unknown> = {}) {
  return requestJson(path, { method: "POST", headers: jsonHeaders, body: JSON.stringify(payload) });
}

export function cancelSubscription() { return postAccountAction("/api/cancel-subscription"); }
export function deleteAccount(confirmation: string) { return postAccountAction("/api/delete-account", { confirmation }); }

export function generateCreative(payload: { tool: string; prompt: string; campaignId?: string; attachmentId?: string }) {
  return requestJson<{ output: string; used: number; limit: number; plan: PlanCode }>("/api/generate", {
    method: "POST", headers: jsonHeaders, body: JSON.stringify(payload),
  });
}
